import { FC } from 'react'

import styled from 'styled-components'
import { IconFileText } from '@tabler/icons-react'

import { InboxDocument, ApprovalStatusEnum } from 'src/Redux/api/documentsApi'
import { Badge } from 'src/Components/Badge'
import { colors, device } from 'src/Helpers/constants'
import Cert from 'src/Assets/certificate.svg?react'
import { IconButton } from 'src/Components/Button/IconButton.tsx'

const Card = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 5px;
  gap: 10px;
  background-color: ${colors.mainGray};
  padding: 10px;
`

const ParametersContainer = styled.div`
  position: relative;
  display: grid;
  grid-column-gap: 16px;
  grid-template-columns: 0.8fr 0.4fr 0.5fr 0.5fr 1fr;

  @media ${device.tabletMax} {
    grid-template-columns: 1fr;
    row-gap: 12px;
  }
`

const NameWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  width: 100%;
  cursor: pointer;

  @media ${device.tabletMax} {
    width: 100%;
  }
`

const IconWrapper = styled.div`
  display: flex;
  width: 50px;
  height: 50px;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const Title = styled.span`
  font-size: 14px;
  color: ${colors.thirdGray};
  font-weight: 600;
`

const Subtitle = styled.span`
  font-size: 12px;
  color: ${colors.thirdGray};
  opacity: 0.7;
`

const Parameter = styled.div`
  font-size: 12px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  color: ${colors.thirdGray};
  font-weight: 600;
  justify-content: center;
  text-align: left;

  @media ${device.tabletMax} {
    text-align: start;
  }
`

const StyledButton = styled(IconButton)`
  background-color: ${colors.fourthBlue};

  &:hover {
    background-color: ${colors.fourthBlue};
  }
`

const BadgeWrapper = styled.div`
  display: flex;
  align-items: center;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;

  @media ${device.tabletMax} {
    justify-content: flex-start;
  }
`

interface InboxDocumentCardProps {
  document: InboxDocument
  onApprove: (documentId: string) => void
  onReject: (documentId: string) => void
  onViewHistory: (documentId: string) => void
}

const getStatusColor = (status: ApprovalStatusEnum): string => {
  switch (status) {
    case ApprovalStatusEnum.IN_PROCESS:
      return 'blue'
    case ApprovalStatusEnum.APPROVED:
      return 'green'
    case ApprovalStatusEnum.REJECTED:
      return 'red'
    case ApprovalStatusEnum.PENDING:
      return 'gray'
    case ApprovalStatusEnum.SKIPPED:
      return 'orange'
    default:
      return 'gray'
  }
}

const getStatusLabel = (status: ApprovalStatusEnum): string => {
  switch (status) {
    case ApprovalStatusEnum.IN_PROCESS:
      return 'На согласовании'
    case ApprovalStatusEnum.APPROVED:
      return 'Согласовано'
    case ApprovalStatusEnum.REJECTED:
      return 'Отклонено'
    case ApprovalStatusEnum.PENDING:
      return 'Ожидает'
    case ApprovalStatusEnum.SKIPPED:
      return 'Пропущено'
    default:
      return 'Неизвестно'
  }
}

export const InboxDocumentCard: FC<InboxDocumentCardProps> = ({
  document,
  onApprove,
}) => {
  const handleDocumentClick = (): void => {
    if (document.contract?.googleDocsUrl) {
      window.open(document.contract.googleDocsUrl, '_blank')
    }
  }

  return (
    <Card>
      <ParametersContainer>
        <NameWrapper onClick={handleDocumentClick}>
          <IconWrapper>
            <IconFileText size={40} color={colors.thirdGray} />
          </IconWrapper>
          <TitleWrapper>
            <Title>{document.contract?.contractNumber || 'Без номера'}</Title>
            <Subtitle>{document.contract?.clientName}</Subtitle>
          </TitleWrapper>
        </NameWrapper>

        <BadgeWrapper>
          <Badge color={getStatusColor(document.approvalItem.status)}>
            {getStatusLabel(document.approvalItem.status)}
          </Badge>
        </BadgeWrapper>

        <Parameter>
          {document.approvalItem.deadline
            ? new Date(document.approvalItem.deadline).toLocaleDateString(
                'ru-RU',
              )
            : '—'}
        </Parameter>

        <Parameter>
          {document.approvalItem.startDate
            ? new Date(document.approvalItem.startDate).toLocaleDateString(
                'ru-RU',
              )
            : '—'}
        </Parameter>

        <ButtonGroup>
          {document.approvalItem.status === ApprovalStatusEnum.IN_PROCESS && (
            <StyledButton
              onClick={() => onApprove(document.id)}
              tooltiptext={'Согласовать'}
            >
              <Cert />
            </StyledButton>
          )}

          {/*<StyledButton*/}
          {/*  onClick={() => onViewHistory(document.id)}*/}
          {/*  tooltiptext={'История'}*/}
          {/*>*/}
          {/*  <IconEye size={16} />*/}
          {/*</StyledButton>*/}
        </ButtonGroup>
      </ParametersContainer>
    </Card>
  )
}
